/**
 * AuthMarketingPanel: the brand panel beside the sign-in / register forms. ~60% wide on large screens, hidden
 * below lg (the forms carry their own compact branding on small screens).
 *
 * RoofVox direction: a Brand Navy ground, one violet glow, the white lockup, an H1 whose last phrase takes the
 * orange -> violet voice gradient, and the waveform mesh anchored bottom-right (the brand's "voice data"
 * motif). Everything is drawn from the theme tokens (`chrome` tokens are always dark, in both colour modes),
 * so it is on-brand by default and follows a tenant's accent/primary. Copy is passed per page so sign-up and
 * login share one treatment.
 *
 * Scale: the panel is sticky and exactly one screen tall, so the hero stays in view even when the form column
 * scrolls (register), and its type/logo grow with the screen (lg -> xl -> 2xl) instead of staying phone-sized
 * inside a 1,100px-wide panel.
 */
import React from 'react';
import BrandLogo from '@/components/UI/BrandLogo';
import WaveMesh from '@/components/Auth/WaveMesh';

interface AuthMarketingPanelProps {
  heading: string;
  /** Set after the heading in the voice gradient (orange -> violet). Use it once, on the payoff phrase. */
  headingAccent?: string;
  subtext: string;
  bullets: string[];
}

const AuthMarketingPanel: React.FC<AuthMarketingPanelProps> = ({ heading, headingAccent, subtext, bullets }) => (
  <div
    className="relative hidden h-dvh w-[60%] flex-shrink-0 select-none flex-col overflow-hidden px-12 py-10 text-chrome-text lg:sticky lg:top-0 lg:flex lg:self-start xl:px-16 xl:py-12 2xl:px-24 2xl:py-16"
  >
    {/* The one violet glow (brand: "violet radial glows signal intelligence and voice activity"). */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -right-32 -top-44 h-[600px] w-[600px] rounded-full 2xl:h-[760px] 2xl:w-[760px]"
      style={{ background: 'radial-gradient(closest-side, rgb(var(--ui-accent) / 0.2), transparent)' }}
    />

    {/* Waveform mesh, faded toward the copy so text always sits on clean navy. */}
    <WaveMesh className="pointer-events-none absolute bottom-0 right-0 h-[52%] w-full xl:h-[62%]" />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          'linear-gradient(90deg, rgb(var(--ui-chrome-from)) 0%, rgb(var(--ui-chrome-from) / 0.9) 44%, transparent 80%)',
      }}
    />

    <div className="relative z-10">
      <BrandLogo surface="dark" fluid className="h-12 xl:h-16 2xl:h-20" />
    </div>

    {/* The copy block is centred in the space under the logo (not pinned to the bottom), then nudged up so
        it clears the waveforms. */}
    <div className="relative z-10 flex flex-1 flex-col justify-center pb-10 xl:pb-16">
      <div className="max-w-lg xl:max-w-xl 2xl:max-w-2xl">
        {/* Deliberately a step under the RoofVox wordmark above it (h-12/16/20), so the logo stays
            the largest thing on the panel and the headline reads as supporting copy. */}
        <h1 className="mb-5 text-balance text-2xl font-semibold leading-[1.2] tracking-tight xl:mb-6 xl:text-3xl xl:leading-[1.15] 2xl:text-4xl 2xl:leading-[1.12]">
          {heading}
          {headingAccent && (
            <>
              {' '}
              <span className="text-gradient-voice-soft">{headingAccent}</span>
            </>
          )}
        </h1>

        <p className="mb-8 max-w-md text-body-lg text-chrome-text2 xl:mb-10 xl:max-w-lg xl:text-lg 2xl:max-w-xl 2xl:text-xl">
          {subtext}
        </p>

        <ul className="space-y-3 xl:space-y-4">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-3 xl:gap-4">
              <span className="mt-[9px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-chrome-primary shadow-[0_0_10px_rgb(var(--ui-primary)/0.7)] xl:mt-[11px] xl:h-2 xl:w-2 2xl:mt-[13px]" />
              <span className="text-body text-chrome-text2 xl:text-base 2xl:text-lg">{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

export default AuthMarketingPanel;
